import React from 'react';
import cx from 'classnames';
import {ResponsivePie} from '@nivo/pie';

import './OpenRateChart.scss';
import {CardTitle} from '@app/components/CardTitle/CardTitle';
import {ErrorLoadingWrapper} from '@app/components/ErrorLoadingWrapper/ErrorLoadingWrapper';
import {RadialContentLoader} from '@app/components/ContentLoaders/RadialContentLoader';
import {transformAggregatedOpenRateToRadialChart} from '@app/utils/ConversationDataTransform';
import {
    getChartColor,
    getLegendIcon,
    DATA_KEYS,
} from '@app/utils/DataRenderingUtils';
import {RadialChartsBottomLegend} from '@app/components/RadialChartsBottomLegend/RadialChartsBottomLegend';
import {ChartDataTypes} from '../Activity';

export interface OpenRateChartProps {
    legendPosition: 'right' | 'bottom';
    conversationsData: ChartDataTypes;
}

export const OpenRateChart: React.FC<OpenRateChartProps> = ({
    legendPosition,
    conversationsData,
}) => {
    const showEmptyState = () => {
        if (
            !conversationsData.data ||
            !conversationsData.data.conversations.counters ||
            conversationsData.data.conversations.counters === 0
        ) {
            return 'No data';
        }
        return null;
    };

    const chartAndLegend = cx({
        ['OpenRateChart__chartAndLegendContainerRow']:
            legendPosition === 'right',
        ['OpenRateChart__chartAndLegendContainerColumn']:
            legendPosition === 'bottom',
    });

    return (
        <div className="OpenRateChart__container">
            <div className="OpenRateChart__content">
                <CardTitle title={'Open rate'} />
                <ErrorLoadingWrapper
                    height={'291px'}
                    error={conversationsData.error}
                    isLoading={conversationsData.loading}
                    CustomLoading={RadialContentLoader}
                    emptyState={showEmptyState()}
                    render={() => {
                        const transformedData =
                            transformAggregatedOpenRateToRadialChart(
                                conversationsData.data.conversations,
                            );
                        const chartData = Object.keys(
                            transformedData.plotData,
                        ).map((k) => ({
                            id: k,
                            label: k,
                            value: transformedData.plotData[k],
                            color: getChartColor(k),
                        }));
                        return (
                            <div className={chartAndLegend}>
                                <div
                                    className={'OpenRateChart__chartContainer'}
                                >
                                    <ResponsivePie
                                        data={chartData}
                                        margin={{
                                            top: 20,
                                            right: 20,
                                            bottom: 20,
                                            left: 20,
                                        }}
                                        sortByValue={false}
                                        innerRadius={0.7}
                                        padAngle={1}
                                        colors={(c: any) =>
                                            c.data.color as string
                                        }
                                        borderWidth={0}
                                        enableArcLabels={false}
                                        enableArcLinkLabels={false}
                                    />
                                </div>
                                <RadialChartsBottomLegend
                                    firstSource={{
                                        value: transformedData.legendData
                                            .openedValue,
                                        ratio: transformedData.legendData
                                            .opened,
                                        label: 'Opened',
                                        color: getChartColor(DATA_KEYS.OPENED),
                                        icon: getLegendIcon('', '', true),
                                    }}
                                    secondSource={{
                                        value: transformedData.legendData
                                            .notOpenedValue,
                                        ratio: transformedData.legendData
                                            .notOpened,
                                        label: 'Not opened',
                                        color: getChartColor(
                                            DATA_KEYS.NOT_OPENED,
                                        ),
                                        icon: getLegendIcon('', '', true),
                                    }}
                                />
                            </div>
                        );
                    }}
                />
            </div>
        </div>
    );
};
